import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import LoyaltyLandingPageBenefitsTableSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageBenefitsTableSection'
import LoyaltyLandingPageHowItWorksSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageHowItWorksSection'
import LoyaltyAccountBenefitsProgressContent from './LoyaltyAccountBenefitsProgressContent'
import LoyaltyLandingPageMovingTiersSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageMovingTiersSection'
import LoyaltyLandingPageFaqSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageFaqSection'
import LoyaltyAccountLandingPageHero from './LoyaltyAccountLandingPageHero'
import LoyaltyLandingPageUSPSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageUSPSection'
import LoyaltyUserBronzeBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserBronzeBenefits'
import LoyaltyUserGoldBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserGoldBenefits'
import LoyaltyUserPlatinumBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserPlatinumBenefits'
import LoyaltyUserSilverBenefits from '../loyaltyLandingPageSharedSections/LoyaltyUserSilverBenefits'
import LoyaltyAccountCurrentOffersSection from './LoyaltyAccountCurrentOffersSection'
import LoyaltyAccountCurrentOffersCarousel from './LoyaltyAccountCurrentOffersCarousel'
import ToggleButton from 'LuxKit/ToggleButton'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import TriplePointTakeoverHeroCarousel from 'takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverHeroCarousel'
import { triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import LoyaltyAccountLandingPageHeroNonMemberRegion from './LoyaltyAccountLandingPageHeroNonMemberRegion'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useLoyaltyCampaignOffers from 'loyalty/hooks/useLoyaltyCampaignOffers'
import useOnLoyaltyPurchasePointsSuccess from 'loyalty/hooks/useOnLoyaltyPurchasePointsSuccess'

const NavSection = styled(Pane)`
  position: sticky;
  z-index: ${zIndex.stickyHeaderBanner};
  top: 0;
  padding: ${rem(12)} 0;
`

const ToggleButtonContainer = styled.div`
  position: relative;
`

const NewBadge = styled.div`
  position: absolute;
  top: ${rem(-4)};
  left: 50%;
  transform: translateX(-50%);
  background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
  color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(1)} ${rem(3)};
  border-radius: ${props => props.theme.borderRadius.L};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  font-size: 10px;
  line-height: 13px;
  pointer-events: none;
`

type LoyaltyAccountTab = 'benefits' | 'offers' | 'about'

function LoyaltyAccountLandingPage() {
  const intl = useIntl()
  const hash = useLocation().hash.replace('#', '')

  const activeTab = useMemo<LoyaltyAccountTab>(() => {
    if (hash.startsWith('benefits')) return 'benefits'
    if (hash.startsWith('offers')) return 'offers'
    if (hash.startsWith('about')) return 'about'
    return 'benefits'
  }, [hash])
  const [tier] = useCurrentLoyaltyTier()
  const [stickyRef, notSticky] = useInView()

  const triplePointCampaignActive = useCurrentSiteTakeover()?.id === triplePointsTakeoverId
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const activeCustomRules = useLoyaltyCampaignOffers()

  useOnLoyaltyPurchasePointsSuccess()
  return <div>
    {isCurrentRegionUserLoyaltyRegion && <LoyaltyAccountLandingPageHero activeTab={activeTab} />}
    {!isCurrentRegionUserLoyaltyRegion && <LoyaltyAccountLandingPageHeroNonMemberRegion />}
    <NavSection shadow={!notSticky ? 'bottom-small' : undefined}>
      <LayoutContainer variant="medium">
        <Group direction="horizontal" horizontalAlign="center" gap={8}>
          <ToggleButton
            orientation="horizontal"
            kind="primary"
            isSelected={activeTab === 'benefits'}
            href="#benefits"
            title={intl.formatMessage({ id: 'UMZaLlL37bhxbPekj0r_', defaultMessage: 'Benefits & progress', description: 'Toggle button title for benefits and progress tab element' })}
          />
          {!!activeCustomRules.length && false &&
            <ToggleButtonContainer>
              <ToggleButton
                orientation="horizontal"
                kind="primary"
                isSelected={activeTab === 'offers'}
                href="#offers"
                title={intl.formatMessage({ id: '2fzjY4oEKd5aVQ-RzEJS', defaultMessage: 'Current offers', description: 'Toggle button title for current offers tab on the offer page' })}
              />
              <NewBadge>{intl.formatMessage({ id: 'RomwpwP6ckPdIvDfk0DP', defaultMessage: 'NEW', description: 'Badge label indicating new content for current offers' })}</NewBadge>
            </ToggleButtonContainer>}
          <ToggleButton
            orientation="horizontal"
            kind="primary"
            isSelected={activeTab === 'about'}
            href="#about"
            title={intl.formatMessage({ id: '7bYtfX3cBSOePppYs1sI', defaultMessage: 'About Société', description: 'Toggle button title for about Société tab on the promotional landing page' })}
          />
        </Group>
      </LayoutContainer>
    </NavSection>
    {activeTab === 'about' && triplePointCampaignActive && <TriplePointTakeoverHeroCarousel
      viewOffersTo="/triple-points"
    />}
    <div ref={stickyRef} />
    {activeTab === 'offers' && <LoyaltyAccountCurrentOffersSection />}
    <Pane background="neutral-seven">
      {activeTab === 'about' && <LoyaltyLandingPageUSPSection />}
      <LayoutContainer variant="medium">
        {activeTab === 'about' && <>
          {tier === 'bronze' && <LoyaltyUserBronzeBenefits />}
          {tier === 'silver' && <LoyaltyUserSilverBenefits />}
          {tier === 'gold' && <LoyaltyUserGoldBenefits />}
          {tier === 'platinum' && <LoyaltyUserPlatinumBenefits /> }
        </>}
        {activeTab === 'benefits' && <LoyaltyAccountBenefitsProgressContent />}
      </LayoutContainer>
    </Pane>
    {activeTab !== 'offers' && <LoyaltyLandingPageMovingTiersSection />}
    {activeTab !== 'offers' && <LoyaltyLandingPageBenefitsTableSection
      highlightTier={tier}
    />}
    {activeTab !== 'offers' && <LoyaltyLandingPageHowItWorksSection />}
    {(activeTab === 'benefits') && <CSSBreakpoint min="tablet">
      <LoyaltyAccountCurrentOffersCarousel />
    </CSSBreakpoint>}
    {activeTab !== 'offers' && <LoyaltyLandingPageFaqSection slug="societe-faqs-member" />}
  </div>
}

export default LoyaltyAccountLandingPage
